import { useState, useEffect } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { C, STATES } from '../constants';
import { isResultsPublished } from '../storage';
import AnonymousBadge from '../components/AnonymousBadge';
import Glass from '../components/Glass';

const STATE_LIST = Object.values(STATES);

export default function StatePickerScreen({ votedStates, onSelect }) {
  const [publishedStates, setPublishedStates] = useState({});

  useEffect(() => {
    Promise.all(
      STATE_LIST.map(s => isResultsPublished(s.id).then(pub => ({ id: s.id, pub })))
    ).then(results => {
      const map = {};
      results.forEach(({ id, pub }) => { map[id] = pub; });
      setPublishedStates(map);
    });
  }, []);
  return (
    <main className="px-5 md:px-10 py-12 md:py-20 max-w-5xl mx-auto">
      <AnonymousBadge />

      <h1 style={{
        fontFamily:    "'Poppins', sans-serif",
        fontWeight:    700,
        fontSize:      'clamp(2rem, 5.5vw, 4rem)',
        lineHeight:    1.05,
        letterSpacing: '-0.025em',
        marginTop:     '1.4rem',
        color:         C.white,
      }}>
        Choose your Party.
        <br />
        <span style={{
          background:          `linear-gradient(90deg, ${C.cyan}, ${C.pink})`,
          WebkitBackgroundClip:'text',
          backgroundClip:      'text',
          color:               'transparent',
          fontStyle:           'italic',
        }}>
          Cast your vote.
        </span>
      </h1>


      {/* CJP mock ballot — full-width featured card (top) */}
      <div className="mt-10">
      {STATE_LIST.filter(s => s.isMock).map(state => {
        const voted = votedStates[state.id];
        return (
          <CJPCard
            key={state.id}
            state={state}
            voted={voted}
            published={publishedStates[state.id] || false}
            onSelect={() => onSelect(state.id)}
          />
        );
      })}
      </div>

      {/* State cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        {STATE_LIST.filter(s => !s.isMock).map(state => {
          const voted = votedStates[state.id];
          return (
            <StateCard
              key={state.id}
              state={state}
              voted={voted}
              published={publishedStates[state.id] || false}
              onSelect={() => onSelect(state.id)}
            />
          );
        })}
      </div>

      {/* Info strip */}
      <div className="mt-12 pt-6 grid grid-cols-1 md:grid-cols-3 gap-4"
           style={{ borderTop: `1px solid ${C.border}` }}>
        {[
          { n: '01', title: 'Pick a state',    body: 'Choose between Tamil Nadu and West Bengal — more states coming soon.' },
          { n: '02', title: 'Vote once',       body: 'One vote per device per state. Your choice is stored only on your device.' },
          { n: '03', title: 'See live results', body: 'The map and charts update in real time as votes pour in statewide.' },
        ].map(s => (
          <Glass key={s.n} style={{ padding: '1rem 1.2rem' }}>
            <div style={{ fontWeight: 700, fontStyle: 'italic', fontSize: '1.4rem', color: C.cyan, lineHeight: 1 }}>
              {s.n}
            </div>
            <div style={{ fontWeight: 700, fontSize: '0.98rem', marginTop: '0.5rem', color: C.white }}>
              {s.title}
            </div>
            <p style={{ fontSize: '0.83rem', color: C.textDim, marginTop: '0.4rem', lineHeight: 1.5 }}>
              {s.body}
            </p>
          </Glass>
        ))}
      </div>
    </main>
  );
}

/* ── CJP mock-ballot card ─────────────────────────────────────────────────── */
const CJP_CREAM   = '#F4EFE0';
const CJP_PAPER   = '#EAE3CE';
const CJP_INK     = '#0A0A0A';
const CJP_INKSOFT = '#3D3A2E';
const CJP_SAFFRON = '#FF6B1A';
const CJP_ROACH   = '#5B3A1F';

function CJPCardLogo() {
  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" width={52} height={52} style={{ display: 'block', flexShrink: 0 }}>
      <circle cx="32" cy="32" r="29" fill="none" stroke="#E0651E" strokeWidth="3" strokeDasharray="46 1000" transform="rotate(-90 32 32)" />
      <circle cx="32" cy="32" r="29" fill="none" stroke="#1F5A2E" strokeWidth="3" strokeDasharray="46 1000" transform="rotate(30 32 32)" />
      <circle cx="32" cy="32" r="29" fill="none" stroke="#2A1A10" strokeWidth="0.8" />
      <ellipse cx="32" cy="36" rx="11" ry="16" fill="#5A2F12" />
      <ellipse cx="32" cy="25" rx="7" ry="6" fill="#5A2F12" />
      <path d="M28 17 Q22 10 18 8 M36 17 Q42 10 46 8" stroke="#2A1A10" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <rect x="26" y="23" width="12" height="3.5" rx="1" fill="#0a0807" />
    </svg>
  );
}

function CJPCard({ state, voted, published, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className="text-left w-full"
      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
    >
      <div style={{
        background:  CJP_CREAM,
        border:      `3px solid ${CJP_INK}`,
        boxShadow:   `6px 6px 0 ${CJP_INK}`,
        overflow:    'hidden',
        transition:  'transform 80ms ease, box-shadow 80ms ease',
        fontFamily:  "'IBM Plex Sans', sans-serif",
      }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translate(-2px,-2px)'; e.currentTarget.style.boxShadow = `8px 8px 0 ${CJP_INK}`; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translate(0,0)';       e.currentTarget.style.boxShadow = `6px 6px 0 ${CJP_INK}`; }}
      >
        {/* Top accent bar */}
        <div style={{
          height:     6,
          background: `linear-gradient(90deg, ${CJP_ROACH}, ${CJP_SAFFRON}, ${CJP_ROACH}88)`,
        }} />

        <div style={{ padding: '1.4rem 1.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>

            {/* Left: logo + text */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
              {/* Logo box */}
              <div style={{
                width: 64, height: 64, flexShrink: 0,
                background:  CJP_PAPER,
                border:      `3px solid ${CJP_INK}`,
                display:     'flex', alignItems: 'center', justifyContent: 'center',
                padding:     6,
              }}>
                <CJPCardLogo />
              </div>

              <div style={{ minWidth: 0 }}>
                {/* Mock badge */}
                <div style={{
                  display:       'inline-flex',
                  alignItems:    'center',
                  gap:           5,
                  padding:       '3px 10px',
                  background:    `${CJP_ROACH}18`,
                  border:        `1.5px solid ${CJP_ROACH}66`,
                  fontSize:      '0.6rem',
                  fontFamily:    "'IBM Plex Sans', sans-serif",
                  fontWeight:    700,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color:         CJP_ROACH,
                  marginBottom:  8,
                }}>
                  🪳 Mock 2029 Lok Sabha
                </div>

                {/* Big headline */}
                <div style={{
                  fontFamily:    "'Arial Black', 'Archivo Black', sans-serif",
                  fontWeight:    900,
                  fontSize:      'clamp(1rem, 2.8vw, 1.5rem)',
                  lineHeight:    1.05,
                  letterSpacing: '-0.02em',
                  color:         CJP_INK,
                  textTransform: 'uppercase',
                }}>
                  Cockroach Janta Party
                </div>
                <div style={{
                  fontSize:      '0.82rem',
                  color:         CJP_INKSOFT,
                  marginTop:     4,
                  fontStyle:     'italic',
                }}>
                  National Mock Election
                </div>

                {published && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    marginTop: 6,
                    padding: '3px 9px',
                    background: 'rgba(14,124,58,0.12)', border: '1.5px solid rgba(14,124,58,0.5)',
                    fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em',
                    textTransform: 'uppercase', color: '#0E7C3A',
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0E7C3A', display: 'inline-block' }} />
                    Results Live
                  </span>
                )}
              </div>
            </div>

            {/* Right: voted checkmark */}
            {voted && (
              <div style={{
                width: 36, height: 36, flexShrink: 0,
                background:  CJP_INK,
                display:     'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Check size={20} color={CJP_CREAM} strokeWidth={3} />
              </div>
            )}
          </div>

          {/* Stats + swatches */}
          <div style={{
            display:     'flex',
            alignItems:  'center',
            gap:         16,
            marginTop:   14,
            paddingTop:  14,
            borderTop:   `2px solid ${CJP_INK}`,
            flexWrap:    'wrap',
          }}>
            <span style={{ fontSize: '0.78rem', color: CJP_INKSOFT }}>
              <strong style={{ fontFamily: "'Arial Black', sans-serif", color: CJP_INK, fontSize: '1rem' }}>{state.districts.length}</strong> states/UTs
            </span>
            <span style={{ color: CJP_INK, opacity: 0.3 }}>·</span>
            <span style={{ fontSize: '0.78rem', color: CJP_INKSOFT }}>
              <strong style={{ fontFamily: "'Arial Black', sans-serif", color: CJP_INK, fontSize: '1rem' }}>{state.parties.length}</strong> parties
            </span>
            <span style={{ color: CJP_INK, opacity: 0.3 }}>·</span>
            <span style={{ fontSize: '0.72rem', color: CJP_INKSOFT, fontStyle: 'italic' }}>Unofficial · Unserious</span>

            {/* Party swatches */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginLeft: 'auto' }}>
              {state.parties.map(p => (
                <span key={p.id} title={p.en} style={{
                  display: 'inline-block', width: 20, height: 7,
                  backgroundColor: p.color,
                  border: `1px solid ${CJP_INK}44`,
                }} />
              ))}
            </div>
          </div>

          {/* CTA row */}
          <div style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            marginTop:      14,
          }}>
            {voted ? (
              <div>
                <div style={{ fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: CJP_ROACH, fontFamily: "'Arial Black', sans-serif" }}>
                  ✓ Already voted
                </div>
                <div style={{ fontSize: '0.78rem', color: CJP_INKSOFT, marginTop: 2 }}>View live national results</div>
              </div>
            ) : published ? (
              <div>
                <div style={{ fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: '#0E7C3A', fontFamily: "'Arial Black', sans-serif" }}>
                  Results published
                </div>
                <div style={{ fontSize: '0.78rem', color: CJP_INK, marginTop: 2, fontWeight: 600 }}>View results →</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: CJP_INKSOFT, fontFamily: "'Arial Black', sans-serif" }}>
                  Not yet voted
                </div>
                <div style={{ fontSize: '0.78rem', color: CJP_INK, marginTop: 2, fontWeight: 700 }}>Cast your mock vote →</div>
              </div>
            )}

            <div style={{
              background:  CJP_INK,
              color:       CJP_CREAM,
              border:      `3px solid ${CJP_INK}`,
              boxShadow:   `3px 3px 0 ${CJP_ROACH}`,
              padding:     '10px 20px',
              fontFamily:  "'Arial Black', sans-serif",
              fontSize:    '0.75rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              display:     'flex', alignItems: 'center', gap: 8,
            }}>
              {voted ? 'Results' : 'Vote now'} <ArrowRight size={14} />
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

/* ── Regular state card ───────────────────────────────────────────────────── */
function StateCard({ state, voted, published, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className="text-left w-full"
      style={{
        background:   'transparent',
        border:       'none',
        padding:      0,
        cursor:       'pointer',
        borderRadius: 20,
      }}
    >
      <Glass
        style={{
          padding:    0,
          overflow:   'hidden',
          border:     `1px solid ${voted ? state.accent + '66' : C.border}`,
          boxShadow:  voted ? `0 0 40px ${state.accent}22` : 'none',
          transition: 'border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease',
        }}
        className="group"
      >
        {/* Accent bar */}
        <div style={{
          height:     4,
          background: `linear-gradient(90deg, ${state.accent}, ${state.accent}55)`,
        }} />

        <div style={{ padding: '1.6rem' }}>
          {/* State name */}
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div style={{
                  fontWeight:    700,
                  fontSize:      '1.7rem',
                  color:         C.white,
                  lineHeight:    1.05,
                  letterSpacing: '-0.02em',
                  fontFamily:    "'Poppins', sans-serif",
                }}>
                  {state.name}
                </div>
                {published && (
                  <span style={{
                    display:        'inline-flex',
                    alignItems:     'center',
                    gap:            5,
                    padding:        '3px 9px',
                    borderRadius:   999,
                    background:     'rgba(34,197,94,0.12)',
                    border:         '1px solid rgba(34,197,94,0.4)',
                    fontSize:       '0.62rem',
                    fontWeight:     700,
                    letterSpacing:  '0.1em',
                    textTransform:  'uppercase',
                    color:          '#22c55e',
                    flexShrink:     0,
                  }}>
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#22c55e',
                      boxShadow: '0 0 6px #22c55e',
                      display: 'inline-block',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }} />
                    Results Live
                  </span>
                )}
              </div>
              <div style={{
                fontFamily: state.localFont,
                fontSize:   '1.05rem',
                color:      state.accent,
                marginTop:  4,
              }}>
                {state.nameLocal}
              </div>
            </div>

            {voted && (
              <span style={{
                width:          34,
                height:         34,
                borderRadius:   '50%',
                background:     state.accent,
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                flexShrink:     0,
                boxShadow:      `0 0 16px ${state.accent}88`,
              }}>
                <Check size={18} color={C.bg} strokeWidth={3} />
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mt-4" style={{ fontSize: '0.78rem', color: C.textDim }}>
            <span>
              <span style={{ fontWeight: 700, color: C.white, fontSize: '1rem' }}>
                {state.districts.length}
              </span>
              {' '}districts
            </span>
            <span style={{ color: C.border }}>·</span>
            <span>
              <span style={{ fontWeight: 700, color: C.white, fontSize: '1rem' }}>
                {state.parties.length}
              </span>
              {' '}parties
            </span>
          </div>

          {/* Party swatches */}
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {state.parties.map(p => (
              <span
                key={p.id}
                title={p.en}
                style={{
                  display:         'inline-block',
                  width:           22,
                  height:          8,
                  borderRadius:    4,
                  backgroundColor: p.color,
                  boxShadow:       `0 0 6px ${p.color}88`,
                }}
              />
            ))}
          </div>

          {/* CTA */}
          <div
            className="flex items-center justify-between mt-5 pt-4"
            style={{ borderTop: `1px solid ${C.border}` }}
          >
            {voted ? (
              <div>
                <div style={{
                  fontSize:   '0.7rem',
                  letterSpacing:'0.16em',
                  textTransform:'uppercase',
                  fontWeight: 600,
                  color:      state.accent,
                }}>
                  ✓ Already voted
                </div>
                <div style={{ fontSize: '0.78rem', color: C.textDim, marginTop: 2 }}>
                  View live results
                </div>
              </div>
            ) : published ? (
              <div>
                <div style={{
                  fontSize:   '0.7rem',
                  letterSpacing:'0.16em',
                  textTransform:'uppercase',
                  fontWeight: 600,
                  color:      '#22c55e',
                }}>
                  Results published
                </div>
                <div style={{ fontSize: '0.78rem', color: C.white, marginTop: 2, fontWeight: 600 }}>
                  View results
                </div>
              </div>
            ) : (
              <div>
                <div style={{
                  fontSize:   '0.7rem',
                  letterSpacing:'0.16em',
                  textTransform:'uppercase',
                  fontWeight: 600,
                  color:      C.textDim,
                }}>
                  Not yet voted
                </div>
                <div style={{ fontSize: '0.78rem', color: C.white, marginTop: 2, fontWeight: 600 }}>
                  Cast your vote
                </div>
              </div>
            )}
            <span
              style={{
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                width:          40,
                height:         40,
                borderRadius:   '50%',
                background:     voted
                  ? state.accent
                  : `linear-gradient(135deg, ${C.cyan}, ${C.pink})`,
                boxShadow: voted
                  ? `0 0 16px ${state.accent}66`
                  : '0 0 16px rgba(0,229,255,0.35)',
                color:          C.bg,
                transition:     'transform 200ms ease',
              }}
            >
              <ArrowRight size={18} />
            </span>
          </div>
        </div>
      </Glass>
    </button>
  );
}
